import { memo, useCallback, useMemo } from 'react';
import { Typography } from 'antd';
import CustomTable from './CustomTable';
import CustomFilter from '../common/CustomFilter';
import { formatIndianStyle } from '../helper/arrayFormatter';

const { Title } = Typography;
const FILL_COLOR = "rgb(59, 130, 246)";

function ProgressView({
	title = "",
	data,
	preferredOrder,
	ignoreFirstIndexing,
	index,
	totalLength,
	sorter = false,
	isDownloadPdf = false,
	isLoading = false,
	onRow,
	rowHoverable = false,
	airportCityMap = {},
}) {
	const shouldShowCityColumn = preferredOrder?.includes("Airport") && !preferredOrder?.includes("CITY");

	const displayOrder = useMemo(() => {
		if (!Array.isArray(preferredOrder)) {
			return [];
		}

		if (!shouldShowCityColumn) {
			return preferredOrder;
		}

		const airportIndex = preferredOrder.indexOf("Airport");
		if (airportIndex < 0) {
			return preferredOrder;
		}

		const withCity = [...preferredOrder];
		withCity.splice(airportIndex + 1, 0, "CITY");
		return withCity;
	}, [preferredOrder, shouldShowCityColumn]);

	const getAirportCode = useCallback((record) => {
		const airportIndex = preferredOrder?.findIndex((item) => item === "Airport");
		if (airportIndex === undefined || airportIndex < 0) {
			return "";
		}

		return String(record?.[airportIndex] || "").trim().toUpperCase();
	}, [preferredOrder]);

	const getRecordValue = useCallback((record, key) => {
		if (key === "CITY") {
			const airportCode = getAirportCode(record);
			return airportCityMap?.[airportCode] || "";
		}

		const valueIndex = preferredOrder?.findIndex((item) => item === key);
		if (valueIndex === undefined || valueIndex < 0) {
			return "";
		}

		return record?.[valueIndex];
	}, [airportCityMap, getAirportCode, preferredOrder]);

	const highestValueMap = useMemo(() => {
		if (!data || data.length === 0) {
			return {};
		}

		const tempHighestValue = {};
		displayOrder?.forEach((key, index) => {
			if (["Airport", "CITY"].includes(key)) {
				tempHighestValue[index] = 0;
				return;
			}

			const numericValues = (data || [])
				.map((row) => Number(getRecordValue(row, key)))
				.filter((value) => Number.isFinite(value));

			numericValues.sort((a, b) => b - a);
			tempHighestValue[index] = numericValues?.[1] || numericValues?.[0] || 0;
		});

		return tempHighestValue;
	}, [data, displayOrder, getRecordValue]);


	const dynamicColumn = () => {
		const columns = displayOrder?.map((key, index) => {
			const sorterFunction = (aValue, bValue) => {
				if ([aValue[0], bValue[0]].includes("TOTAL")) {
					return 0
				}
				const leftValue = getRecordValue(aValue, key);
				const rightValue = getRecordValue(bValue, key);
				if (typeof leftValue === "string" && typeof rightValue === "string") {
					return leftValue.localeCompare(rightValue);
				}

				if (!isNaN(leftValue) && !isNaN(rightValue)) {
					return leftValue - rightValue;
				}
				else {
					return 0;
				}
			}

			const sorterConfig = sorter ? { sorter: sorterFunction } : undefined;

			return {
				title: <CustomFilter key={index}
					title={key?.replace(/_/g, ' ')?.toUpperCase()}
				/>,
				align: "center",
				dataIndex: index,
				key: index,
				render: (text, record) => {
					const value = getRecordValue(record, key);
					let highest = highestValueMap?.[index]
					let curPerc = 0;

					if (key?.endsWith("%")) {
						curPerc = value === "100" ? "0" : value;
					}
					else if (highest > 0) {
						curPerc = value / highest * 100
						if (curPerc > 100) {
							curPerc = 0
						}
					}

					if (record[0] === "TOTAL" || key === "Total Pax") {
						curPerc = 0
					}

					return <div style={{ flex: 1, position: "relative" }}>
						{!['Airport', 'CITY'].includes(key) && <div style={{
							zIndex: 1, flex: 1, width: `${curPerc}%`, height: "100%", borderRadius: "5px",
							position: "absolute", backgroundColor: "rgb(59 163 246 / 90%)",
							top: isDownloadPdf ? 6 : 0
						}}>
						</div>}
						<div style={{ flex: 1, position: "relative", zIndex: 2, marginRight: "5px", marginTop: "-1px", marginBottom: "-1px" }}>
							<p className="text-right">{formatIndianStyle(value) + (key?.endsWith("%") ? "%" : "")}</p>
						</div>
					</div>
				},
				...sorterConfig
			}
		})

		return columns
	}


	return (
		<div>
			<Title level={5} style={{
				background: FILL_COLOR, borderRadius: "5px 20px", marginLeft: "15px",
				paddingInline: "35px", color: "#FFF", width: "fit-content", transition: "height 0.5s ease",
				marginTop: 20, marginBottom: 20, height: isDownloadPdf ? 32 : 25, boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
			}}>
				{title}
			</Title>

			<div style={index > 0 && index < totalLength - 1 ?
				{ overflow: "hidden", marginInline: '6px', "-webkit-box-shadow": '0 4px 10px rgba(0, 0, 0, 0.2)', borderRadius: 15 }
				:
				{ overflow: "hidden", marginInline: '6px', "-webkit-box-shadow": '0 4px 10px rgba(0, 0, 0, 0.2)', borderRadius: 15 }}>
				<CustomTable
					data={data}
					columns={dynamicColumn()}
					bordered={false}
					loading={isLoading}
					scroll={{ x: true }}
					pagination={false}
					onRow={onRow}
					rowHoverable={rowHoverable}
				/>
			</div>
		</div>
	)
}

export default memo(ProgressView)
